import { FC, useState, useCallback, useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate, useLocation } from 'react-router-dom'
import styles from 'styles/components/template.module.scss'
import DataLoad from 'components/Init/DataLoad'
import PageHedding from 'components/PageHedding/PageHedding'
import InputTextWithLabel from 'components/Form/WithLabel/InputText'
import SubmitButton from 'components/Button/SubmitButton'
import CheckBoxWithLabel from 'components/Form/WithLabel/CheckBox'
import { setIsLoading } from 'reducks/loading/slice'
import {
  getWholesalerById,
  createWholesaler,
  updateWholesaler,
  clearWholesaler,
  WholesalerType,
} from 'reducks/wholesaler/slice'
import { RootState } from 'reducks/reducers'

const WholesalerOperationTemplate: FC = () => {
  const dispatch = useDispatch<any>()
  const navigate = useNavigate()
  const location = useLocation()
  const path = location.pathname
  const wholesalerId = path.split('/wholesaler/')[1]?.split('/')[1] || null
  const { staff } = useSelector((state: RootState) => state.staffSlice)

  const [name, setName] = useState<string>('')
  const [isActive, setIsActive] = useState<boolean>(true)

  // Validation
  const [nameErrorText, setNameErrorText] = useState<string | null>(null)

  const handleName = (name: string) => {
    setName(name)
    setNameErrorText(null)
  }

  const handleIsActiveChange = () => {
    setIsActive(!isActive)
  }

  const validation = (): boolean => {
    let isError = false
    if (!name) {
      isError = true
      setNameErrorText('問屋名を入力してください')
    }
    if (isError) {
      window.scrollTo(0, 0)
      return false
    }
    return true
  }

  const handleSubmit = async () => {
    if (!validation()) {
      return
    }

    dispatch(setIsLoading({ isLoading: true }))

    try {
      if (wholesalerId) {
        // 更新
        await dispatch(
          updateWholesaler({
            id: wholesalerId,
            name: name,
            is_active: isActive,
          }),
        )
        alert('問屋情報を更新しました')
      } else {
        // 新規作成
        await dispatch(
          createWholesaler({
            name: name,
          }),
        )
        alert('問屋を登録しました')
      }

      navigate('/wholesaler')
      window.scrollTo(0, 0)
    } catch (error) {
      console.error('問屋登録エラー:', error)
      const errorMessage =
        error instanceof Error
          ? `登録に失敗しました: ${error.message}`
          : '登録に失敗しました。もう一度お試しください。'
      alert(errorMessage)
    } finally {
      dispatch(setIsLoading({ isLoading: false }))
    }
  }

  const setWholesalerInfo = useCallback(
    async (wholesalerId: string | null) => {
      if (!staff) {
        navigate('/initload?path=' + path)
        window.scrollTo(0, 0)
        return
      }
      if (!staff.isAdmin) {
        navigate('/initload')
        window.scrollTo(0, 0)
        return
      }

      await dispatch(clearWholesaler())

      if (wholesalerId) {
        const wholesalerPayload = await dispatch(
          getWholesalerById({ id: wholesalerId }),
        )
        const wholesaler: WholesalerType = wholesalerPayload.payload
        if (wholesaler) {
          setName(wholesaler.name)
          setIsActive(wholesaler.is_active)
        }
      }
    },
    [staff, navigate, path, dispatch],
  )

  useEffect(() => {
    setWholesalerInfo(wholesalerId)
  }, [setWholesalerInfo, wholesalerId])

  return (
    <div className={styles.container}>
      <DataLoad />
      <PageHedding
        title={wholesalerId ? '問屋編集' : '問屋新規登録'}
        to="/wholesaler"
      />

      <InputTextWithLabel
        required={true}
        labelName="問屋名"
        description=""
        line={1}
        type="text"
        size="large"
        value={name}
        placeholder="富澤商店"
        labelSize="small"
        errorText={nameErrorText}
        onChange={(event) => handleName(event.target.value)}
      />

      <CheckBoxWithLabel
        required={false}
        labelName="ステータス"
        description=""
        labelPosition="left"
        line={1}
        checkInfos={[
          {
            isChecked: isActive,
            label: '有効',
            size: 'large',
            type: 'single',
            onChange: handleIsActiveChange,
          },
        ]}
      />

      <div className={styles.material_submit}>
        <SubmitButton
          label="キャンセル"
          color="orange_outlined"
          size="large"
          icon="none"
          onClick={() => navigate('/wholesaler')}
        />
        <span className={styles.material_submit_spacer}></span>
        <SubmitButton
          label={wholesalerId ? '更新する' : '登録する'}
          color="orange"
          size="large"
          icon="none"
          onClick={handleSubmit}
        />
      </div>
    </div>
  )
}

export default WholesalerOperationTemplate
